import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon, type TableColumn } from '../ui';

/**
 * The content queue's table. Same markup and tokens as ui/Table (dense), which can't host sortable
 * rows: dnd-kit needs a ref and a transform on each <tr>. In queue order a drag handle leads every
 * row; only the handle starts a drag, so a click anywhere else still opens the row.
 */

type RowBase = { id: string };

interface QueueTableProps<Row extends RowBase> {
  columns: TableColumn<Row>[];
  rows: Row[];
  /** Queue order view. Off while a column sort is on, since a drag there would mean nothing. */
  sortable: boolean;
  onReorder: (activeId: string, overId: string) => void;
  onRowClick?: (row: Row) => void;
  style?: React.CSSProperties;
}

const CELL_PAD = '5px 10px';
const HANDLE_WIDTH = '30px';

interface QueueRowProps<Row extends RowBase> {
  row: Row;
  columns: TableColumn<Row>[];
  sortable: boolean;
  isLast: boolean;
  onRowClick?: (row: Row) => void;
}

function SortableQueueRow<Row extends RowBase>({ row, columns, sortable, isLast, onRowClick }: QueueRowProps<Row>) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: !sortable,
  });

  return (
    <tr
      ref={setNodeRef}
      className="hover-wash"
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 2 : undefined,
        background: isDragging ? 'var(--surface-raised)' : undefined,
        boxShadow: isDragging ? 'var(--shadow-pop)' : undefined,
        cursor: onRowClick ? 'pointer' : 'default',
        borderBottom: isLast ? 'none' : '1px solid var(--border-hairline)',
      }}
    >
      {sortable ? (
        <td style={{ padding: '5px 0 5px 8px' }}>
          <button
            ref={setActivatorNodeRef}
            type="button"
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
            title="Drag to reorder"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '22px',
              padding: 0,
              border: 'none',
              background: 'transparent',
              color: 'var(--text-faint)',
              cursor: isDragging ? 'grabbing' : 'grab',
              touchAction: 'none',
            }}
          >
            <Icon name="grip-vertical" size={12} />
          </button>
        </td>
      ) : null}
      {columns.map((col) => {
        const cell = (row as unknown as Record<string, unknown>)[col.key];
        return (
          <td
            key={col.key}
            className={col.mono ? 't-num' : ''}
            style={{
              textAlign: col.align || 'left',
              padding: CELL_PAD,
              fontSize: col.mono ? 'var(--text-xs)' : 'var(--text-sm)',
              color: 'var(--text-body)',
              whiteSpace: col.wrap ? 'normal' : 'nowrap',
              overflowWrap: col.wrap ? 'anywhere' : undefined,
              verticalAlign: col.wrap ? 'top' : undefined,
            }}
          >
            {typeof col.render === 'function' ? col.render(cell, row) : (cell as React.ReactNode)}
          </td>
        );
      })}
    </tr>
  );
}

export default function QueueTable<Row extends RowBase>({ columns, rows, sortable, onReorder, onRowClick, style }: QueueTableProps<Row>) {
  const sensors = useSensors(
    // A few px of travel before a drag starts, so a click on the handle stays a click.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  };

  return (
    <div
      className="ck-table"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-rest)',
        overflow: 'hidden',
        ...style,
      }}
    >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            {sortable ? <col style={{ width: HANDLE_WIDTH }} /> : null}
            {columns.map((col) => (
              <col key={col.key} style={col.width ? { width: col.width } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {sortable ? (
                <th aria-label="Reorder" style={{ padding: CELL_PAD, borderBottom: '1px solid var(--border-hairline)', background: 'var(--surface-raised)' }} />
              ) : null}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="t-eyebrow"
                  style={{
                    textAlign: col.align || 'left',
                    padding: CELL_PAD,
                    borderBottom: '1px solid var(--border-hairline)',
                    background: 'var(--surface-raised)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
            <tbody>
              {rows.map((row, i) => (
                <SortableQueueRow
                  key={row.id}
                  row={row}
                  columns={columns}
                  sortable={sortable}
                  isLast={i === rows.length - 1}
                  onRowClick={onRowClick}
                />
              ))}
            </tbody>
          </SortableContext>
        </table>
      </DndContext>
    </div>
  );
}
